import React, { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { captureException } from '../lib/errorReporter';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional tag so reports can be filtered per-app in the dashboard. */
  app?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, errorInfo);
    captureException(error, {
      app: this.props.app,
      componentStack: errorInfo.componentStack ?? undefined,
      tags: { source: 'error-boundary' },
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-[40vh] flex items-center justify-center p-8" role="alert">
          <div className="text-center space-y-4 max-w-md">
            <p className="text-4xl" aria-hidden="true">⚠️</p>
            <h2 className="text-xl font-bold text-stone-900">Something went wrong</h2>
            <p className="text-sm text-stone-500">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-block px-6 py-2 bg-stone-900 text-white text-xs font-bold uppercase tracking-widest hover:bg-stone-800 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
